import React, { useState } from "react";
import { useEffect } from "react";
import { signInWithEmailAndPassword, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc, query, where, collection, getDocs } from 'firebase/firestore';
import { auth, firestore } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

export default function Login() {
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setEmailOrUsername("");
    setPassword("");
  }, []);

  const handleLogin = async () => {
    if (!emailOrUsername || !password) {
      alert("Email/Username dan password wajib diisi!");
      return;
    }

    setLoading(true);
    try {
      let email = emailOrUsername;
      // Jika input bukan email, anggap username, cari emailnya di firestore
      if (!emailOrUsername.includes("@")) {
        const usersRef = collection(firestore, "Users");
        const q = query(usersRef, where("username", "==", emailOrUsername));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          email = userDoc.data().email;
        } else {
          throw new Error("Username tidak ditemukan");
        }
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log("Login Firebase berhasil");
      console.log(userCredential.user);
      const token = await userCredential.user.getIdToken(true);
      console.log(token);
      console.log(token);

      // Ambil role user dari Firestore
      const userDoc = await getDoc(doc(firestore, "Users", user.uid));
      const role = userDoc.data()?.role || "user";

      // Redirect berdasarkan role
      if (role === "admin" || role === "teknisi") {
        navigate("/dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      if (error.code === "auth/invalid-credential") {
        alert("Email atau password salah. Pastikan akun sudah dibuat dan email diverifikasi.");
      } else {
        alert("Error: " + error.message);
      }
    } finally {
      setLoading(false);
    } 
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Cek jika user sudah ada di Firestore
      const userDocRef = doc(firestore, "Users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) {
        // Simpan user baru default role 'user'
        await setDoc(userDocRef, {
          email: user.email,
          username: user.displayName || " ",
          role: "user",
          photoURL: user.photoURL || " ",
          createdAt: new Date(),
        });
      }

      // Redirect ke dashboard sesuai role (ambil role baru)
      const roleDoc = await getDoc(userDocRef);
      const role = roleDoc.data()?.role || "user";
      if (role === "admin" || role === "teknisi") {
        navigate("/dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      alert("Google Sign-In gagal: " + error.message);
    }
  };

  const handleForgotPassword = async () => {
    if (!emailOrUsername) {
      alert("Masukkan email untuk reset password");
      return;
    }
    // Reset password harus pakai email
    let email = emailOrUsername;
    if (!email.includes("@")) {
      alert("Reset password hanya bisa menggunakan email");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      alert("Link reset password telah dikirim ke email Anda");
    } catch (error) {
      alert("Error reset password: " + error.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      {/* Background blur */}
      <div className="absolute inset-0 bg-[radial-gradient(at_50%_30%,rgba(16,185,129,0.15),transparent)]"/>

      <div className="relative w-full max-w-[350px] bg-white rounded-4xl border-4 border-white shadow-custom p-6 md:p-9">
        <button
          onClick={() => navigate("/cover")}
          className="flex items-center text-lg text-primary disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="text-center text-3xl font-black text-primary mb-2">Log In</h1>

        {/* Don't have an account? */}
        <div className="text-center mt-4">
          <p className="text-sm text-gray-700">
            Don't have an account?{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate("/signup");
              }}
              className="text-primaryDark font-semibold no-underline transition-colors duration-300 hover:text-primary"
            >
              Register
            </a>
          </p>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); }} className="mt-5 space-y-4">
          <input
            required
            type="text"
            autoComplete="off"
            name="email"
            value={emailOrUsername}
            onChange={(e) => setEmailOrUsername(e.target.value)}
            placeholder="E-mail or Username"
            className="w-full bg-white border-none rounded-full px-5 py-4 shadow-input focus:outline-none focus:ring-2 focus:ring-primaryDark"
          />
          <input
            required
            type="password"
            autoComplete="new-password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full bg-white border-none rounded-full px-5 py-4 shadow-input focus:outline-none focus:ring-2 focus:ring-primaryDark"
          />
          <div className="text-right">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-xs text-primaryDark font-normal no-underline bg-transparent border-none cursor-pointer transition-colors duration-300 hover:text-primary"
            >
              Forgot Password?
            </button>
          </div>
          <button
            type="submit"
            onClick={handleLogin}
            disabled={loading}
            className="w-full font-bold bg-gradient-to-r from-emerald-600 to-cyan-500 text-white py-4 rounded-full shadow-btn hover:scale-[1.03] active:scale-[0.95] transition-transform duration-200"
          >
            {loading ? "Memproses..." : "Log In"}
          </button>
        </form>

        {/* Social login */}
        <div className="mt-6">
          <p className="text-center text-sm text-gray-500">Or</p>
          <div className="flex justify-center gap-4 mt-3">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full h-12 rounded-full bg-gradient-to-r from-black to-gray-700 border-4 border-white shadow-social flex items-center justify-center hover:scale-[1.03] active:scale-[0.95] transition-transform duration-200"
            >
              <a className="text-center text-white font-semibold px-2">Log in with</a>
              <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 488 512" className="fill-white w-5 h-5">
                <path d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}